import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Filter, MapPin, Package, Building, X } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import Sidebar from '../components/Sidebar';

const numFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));

/**
 * خريطة التوريدات التفاعلية — Supply Intelligence Map.
 * Uses /api/supply/schools aggregate + parses coords strings client-side.
 * Filters: supplier, education admin (governorate), city (region), stage.
 */
export default function MapPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState('');
  const [gov, setGov] = useState('');
  const [city, setCity] = useState('');
  const [stage, setStage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/supply/schools`);
      const j = await r.json();
      // parse coords
      const parsed = (j.schools || []).map((s) => {
        let lat = null, lng = null;
        if (s.coords && typeof s.coords === 'string' && s.coords.includes(',')) {
          const [a, b] = s.coords.split(',').map((x) => parseFloat(x.trim()));
          if (!isNaN(a) && !isNaN(b) && Math.abs(a) <= 90 && Math.abs(b) <= 180) {
            lat = a; lng = b;
          }
        }
        return { ...s, lat, lng };
      });
      setRows(parsed);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suppliers = useMemo(() => Array.from(new Set(rows.map((r) => r.supplier).filter(Boolean))).sort(), [rows]);
  const govs      = useMemo(() => Array.from(new Set(rows.map((r) => r.governorate).filter(Boolean))).sort(), [rows]);
  const cities    = useMemo(() => Array.from(new Set(rows.map((r) => r.region).filter(Boolean))).sort(), [rows]);
  const stages    = useMemo(() => Array.from(new Set(rows.map((r) => r.stage).filter(Boolean))).sort(), [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (supplier && r.supplier !== supplier) return false;
    if (gov && r.governorate !== gov) return false;
    if (city && r.region !== city) return false;
    if (stage && r.stage !== stage) return false;
    return true;
  }), [rows, supplier, gov, city, stage]);

  const withCoords = useMemo(() => filtered.filter((r) => r.lat != null && r.lng != null), [filtered]);
  const missingCoords = filtered.length - withCoords.length;

  const summary = useMemo(() => {
    const uniqueSup = new Set(); let totalQty = 0; let totalValue = 0;
    for (const r of filtered) {
      if (r.supplier) uniqueSup.add(r.supplier);
      totalQty += Number(r.allocated_qty) || 0;
      totalValue += Number(r.allocated_value) || 0;
    }
    return { schools: withCoords.length, filteredTotal: filtered.length, suppliers: uniqueSup.size, totalQty, totalValue };
  }, [filtered, withCoords]);

  const clearFilters = () => { setSupplier(''); setGov(''); setCity(''); setStage(''); };
  const anyFilter = supplier || gov || city || stage;

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="map-page">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>SUPPLY INTELLIGENCE MAP</div>
            <h1 className="se-title mt-1">خريطة التوريدات التفاعلية</h1>
            <div className="se-subtitle">استعرض توزيع المدارس جغرافياً واستخدم الفلاتر للتركيز على مورد أو إدارة تعليم</div>
          </div>
        </div>

        {/* Filters */}
        <div className="se-panel p-4 mb-4" data-testid="map-filters">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={14} style={{ color: '#B7ADFF' }} />
            <div className="text-[13px] font-bold" style={{ color: '#F1EEFB' }}>الفلاتر التفاعلية</div>
            {anyFilter && (
              <button className="se-btn se-btn-ghost mr-auto" onClick={clearFilters}>
                <X size={12} /> مسح الكل
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <select className="se-input" value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="filter-supplier">
              <option value="">جميع الموردين ({suppliers.length})</option>
              {suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="se-input" value={gov} onChange={(e) => setGov(e.target.value)} data-testid="filter-gov">
              <option value="">جميع إدارات التعليم ({govs.length})</option>
              {govs.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="se-input" value={city} onChange={(e) => setCity(e.target.value)} data-testid="filter-city">
              <option value="">جميع المدن ({cities.length})</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="se-input" value={stage} onChange={(e) => setStage(e.target.value)} data-testid="filter-stage">
              <option value="">جميع المراحل ({stages.length})</option>
              {stages.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="map-summary">
          <MiniStat label="مدارس ظاهرة" value={numFmt(summary.schools)} tint="#9D8CFF" icon={MapPin} />
          <MiniStat label="مدارس بلا إحداثيات" value={numFmt(missingCoords)} tint="#FBBF24" icon={MapPin} warn={missingCoords > 0} />
          <MiniStat label="عدد الموردين" value={numFmt(summary.suppliers)} tint="#B7ADFF" icon={Building} />
          <MiniStat label="إجمالي الكمية المخصصة" value={numFmt(summary.totalQty)} tint="#38D6C7" icon={Package} />
          <MiniStat label="القيمة الإجمالية" value={moneyFmt(summary.totalValue)} unit="ريال" tint="#38D6C7" icon={Package} />
        </div>

        {/* Map */}
        {loading ? (
          <div className="se-panel p-12 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : withCoords.length === 0 ? (
          <div className="se-panel p-12 text-center text-slate-400">لا توجد إحداثيات صالحة في البيانات المستوردة لعرض الخريطة.</div>
        ) : (
          <div className="se-panel p-3" style={{ height: 560 }}>
            <MapContainer center={[withCoords[0].lat, withCoords[0].lng]} zoom={7} scrollWheelZoom
              style={{ height: '100%', width: '100%', borderRadius: 12, background: '#0F0E15' }}>
              <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' className="se-map-tiles" />
              {withCoords.map((m, i) => (
                <CircleMarker key={i} center={[m.lat, m.lng]} radius={5}
                  pathOptions={{ color: '#9D8CFF', fillColor: '#9D8CFF', fillOpacity: 0.7, weight: 1.5 }}>
                  <Popup>
                    <div dir="rtl" style={{ minWidth: 240 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{m.school_name || m.school_id}</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>الرقم الوزاري: {m.school_id}</div>
                      {m.supplier && <div style={{ fontSize: 11, color: '#64748B' }}>المورد: {m.supplier}</div>}
                      {m.governorate && <div style={{ fontSize: 11, color: '#64748B' }}>الإدارة: {m.governorate}</div>}
                      {m.region && <div style={{ fontSize: 11, color: '#64748B' }}>المدينة: {m.region}</div>}
                      {m.stage && <div style={{ fontSize: 11, color: '#64748B' }}>المرحلة: {m.stage}</div>}
                      <div style={{ fontSize: 12, marginTop: 6, color: '#6D5DFB', fontWeight: 700 }}>
                        {numFmt(m.items_count)} صنف · {numFmt(m.allocated_qty)} قطعة
                      </div>
                      {m.allocated_value > 0 && (
                        <div style={{ fontSize: 11, color: '#38D6C7' }}>القيمة: {moneyFmt(m.allocated_value)} ريال</div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        )}
      </main>
    </div>
  );
}

function MiniStat({ label, value, unit, tint, icon: Icon, warn }) {
  return (
    <div className="se-panel p-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}22`, border: `1px solid ${tint}44` }}>
        <Icon size={14} style={{ color: tint }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-slate-400">{label}</div>
        <div className="text-[16px] font-extrabold tabular-nums" style={{ color: warn ? '#FBBF24' : '#F1EEFB' }}>{value}</div>
        {unit && <div className="text-[9.5px] text-slate-500">{unit}</div>}
      </div>
    </div>
  );
}
